import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import ImageLightbox from './ImageLightbox'

const slides = [
  {
    src: '/images/screen-captures/interview/conversation-01.png',
    alt: 'PrismScope adaptive conversation — opening questions',
  },
  {
    src: '/images/screen-captures/interview/conversation-02.png',
    alt: 'PrismScope adaptive conversation with follow-up questions',
  },
  {
    src: '/images/screen-captures/interview/conversation-03.png',
    alt: 'PrismScope adaptive conversation with deeper follow-ups',
  },
  {
    src: '/images/screen-captures/interview/conversation-04.png',
    alt: 'PrismScope conversation exploring customer sentiment',
  },
  {
    src: '/images/screen-captures/interview/conversation-05.png',
    alt: 'PrismScope conversation uncovering actionable insights',
  },
  {
    src: '/images/screen-captures/admin/dashboard.png',
    alt: 'PrismScope admin dashboard with real-time analytics',
  },
]

export default function ImageCarousel() {
  const [current, setCurrent] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length)
  }, [])

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length)
  }, [])

  useEffect(() => {
    if (isPaused) return
    const timer = setInterval(next, 5000)
    return () => clearInterval(timer)
  }, [isPaused, next])

  return (
    <div
      className="relative max-w-4xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Main image */}
      <div className="relative overflow-hidden rounded-xl border border-intelligence-200 shadow-sm">
        <ImageLightbox src={slides[current].src} alt={slides[current].alt}>
          <img
            src={slides[current].src}
            alt={slides[current].alt}
            className="w-full h-auto cursor-pointer"
          />
        </ImageLightbox>

        {/* Arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); prev() }}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow transition-colors"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-5 h-5 text-intelligence-700" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); next() }}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow transition-colors"
          aria-label="Next image"
        >
          <ChevronRight className="w-5 h-5 text-intelligence-700" />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              i === current ? 'bg-primary-600' : 'bg-intelligence-300 hover:bg-intelligence-400'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Thumbnail strip */}
      <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
        {slides.map((slide, i) => (
          <button
            key={slide.src}
            onClick={() => setCurrent(i)}
            className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
              i === current
                ? 'border-primary-600 shadow-sm'
                : 'border-transparent opacity-60 hover:opacity-100'
            }`}
          >
            <img
              src={slide.src}
              alt={slide.alt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
    </div>
  )
}
